import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xxl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #ef4444;
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const ReasonText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const Button = styled.button`
  margin-top: ${({ theme }) => theme.spacing.md};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
`;

/**
 * Recibe el redirect de CoopenAuth después del flow de Google. Llama al
 * bootstrap del backend (que consume la cookie `coopen_session`) y guarda
 * token+user en localStorage vía useAuth.loginWithCoopen.
 */
export function CoopenCallbackPage() {
  const { loginWithCoopen } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        await loginWithCoopen();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not complete SSO';
        setError(msg);
      }
    })();
  }, [loginWithCoopen, navigate]);

  return (
    <Page>
      <Card>
        {!error ? (
          <>
            <Title>Signing in…</Title>
            <Subtitle>Connecting your Coopen account.</Subtitle>
          </>
        ) : (
          <>
            <Title>Could not sign in</Title>
            <ErrorText>{error}</ErrorText>
            <ReasonText>{extractReason(error)}</ReasonText>
            <Button onClick={() => navigate('/login', { replace: true })}>
              Back to login
            </Button>
          </>
        )}
      </Card>
    </Page>
  );
}

function extractReason(msg: string): string {
  if (msg.includes('inválido') || msg.includes('expirado')) {
    return 'Si esto persiste, los JWT_SECRET de CoopenAuth y Marketing no coinciden — avisar a infra.';
  }
  if (msg.includes('cookie') || msg.includes('token')) {
    return 'No llegó la cookie coopen_session — probá entrar primero a auth.coopenstudio.com.';
  }
  return '';
}
